import { useEffect, useMemo, useRef, useState } from 'react';

type GoogleMapsWindow = Window & {
  google?: {
    maps?: {
      Geocoder: new () => {
        geocode: (
          request: { address: string; language?: string },
          callback: (results: GoogleGeocodeResult[] | null, status: string) => void,
        ) => void;
      };
      places?: {
        AutocompleteSuggestion?: {
          fetchAutocompleteSuggestions: (
            request: {
              input: string;
              includedRegionCodes?: string[];
              includedPrimaryTypes?: string[];
              language?: string;
            },
          ) => Promise<{
            suggestions?: GoogleAutocompleteSuggestion[];
          }>;
        };
        AutocompleteService?: new () => {
          getPlacePredictions: (
            request: {
              input: string;
              types?: string[];
              componentRestrictions?: { country: string };
              language?: string;
            },
            callback: (
              predictions: GooglePlacePrediction[] | null,
              status: string,
            ) => void,
          ) => void;
        };
      };
    };
  };
};

type GoogleGeocodeResult = {
  types?: string[];
  address_components?: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
};

type GooglePlacePrediction = {
  description?: string;
  structured_formatting?: {
    main_text?: string;
  };
};

type GoogleAutocompleteSuggestion = {
  placePrediction?: {
    text?: string | { toString: () => string };
    mainText?: string | { toString: () => string };
    structuredFormat?: {
      mainText?: string | { toString: () => string };
    };
  };
};

type UseAddressAutocompleteParams = {
  apiKey: string;
  language: string;
  city: string;
  postcode: string;
  addressLine1: string;
  selectedCountryCode?: string;
  selectedCountryName?: string;
  cityDropdownOpen: boolean;
  maxEmptyStreetSuggestions?: number;
};

type UseAddressAutocompleteResult = {
  cityDropdownOptions: string[];
  streetSuggestions: string[];
};

const GOOGLE_PLACES_SCRIPT_ID = 'google-maps-places-script';

function getDisplayValue(value?: unknown) {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : '';
}

function loadGoogleMapsPlacesScript(apiKey: string, language: string) {
  return new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_PLACES_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      if ((window as GoogleMapsWindow).google?.maps?.places) {
        resolve();
      } else {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps script')), { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_PLACES_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places${language ? `&language=${encodeURIComponent(language)}` : ''}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  });
}

function getAddressComponent(
  result: GoogleGeocodeResult,
  types: string[],
) {
  const components = result.address_components ?? [];
  const component = components.find((item) => types.some((type) => item.types.includes(type)));
  return component?.long_name;
}

function getAddressComponentShort(
  result: GoogleGeocodeResult,
  types: string[],
) {
  const components = result.address_components ?? [];
  const component = components.find((item) => types.some((type) => item.types.includes(type)));
  return component?.short_name;
}

function extractCityFromGeocodeResult(result: GoogleGeocodeResult) {
  return (
    getAddressComponent(result, ['locality'])
    ?? getAddressComponent(result, ['postal_town'])
    ?? getAddressComponent(result, ['sublocality_level_1'])
    ?? getAddressComponent(result, ['administrative_area_level_3'])
    ?? ''
  );
}

function normalizePostcode(value: string) {
  return value.replace(/[^a-z0-9]/gi, '').toUpperCase();
}

function getGeocodeCityScore(
  result: GoogleGeocodeResult,
  {
    postcode,
    countryCode,
  }: {
    postcode: string;
    countryCode: string;
  },
) {
  let score = 0;

  const geocodePostcode = normalizePostcode(getAddressComponent(result, ['postal_code']) ?? '');
  const geocodeCountryCode = getDisplayValue(getAddressComponentShort(result, ['country'])).toLowerCase();
  const hasCity = Boolean(extractCityFromGeocodeResult(result).trim());

  if (postcode && geocodePostcode && geocodePostcode === postcode) {
    score += 8;
  }

  if (countryCode && geocodeCountryCode && geocodeCountryCode === countryCode) {
    score += 4;
  }

  if (hasCity) {
    score += 2;
  }

  if ((result.types ?? []).includes('postal_code')) {
    score += 1;
  }

  return score;
}

function toSuggestionText(value: unknown) {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (
    value
    && typeof value === 'object'
    && 'toString' in value
    && typeof value.toString === 'function'
  ) {
    return value.toString().trim();
  }

  return '';
}

function parseAutocompleteSuggestions(suggestions: GoogleAutocompleteSuggestion[] = []) {
  return suggestions
    .map((item) => (
      toSuggestionText(item.placePrediction?.mainText)
      || toSuggestionText(item.placePrediction?.structuredFormat?.mainText)
      || toSuggestionText(item.placePrediction?.text)
    ))
    .filter(Boolean);
}

function getPlacePredictions({
  input,
  types,
  countryCode,
  language,
}: {
  input: string;
  types: string[];
  countryCode?: string;
  language?: string;
}) {
  return new Promise<string[]>((resolve) => {
    const mapsWindow = window as GoogleMapsWindow;
    const places = mapsWindow.google?.maps?.places;
    if (!places) {
      resolve([]);
      return;
    }

    if (places.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
      const includedPrimaryTypes = types.includes('(cities)')
        ? ['(cities)']
        : types.includes('address')
          ? ['route']
          : undefined;

      places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        ...(countryCode ? { includedRegionCodes: [countryCode] } : {}),
        ...(includedPrimaryTypes ? { includedPrimaryTypes } : {}),
        ...(language ? { language } : {}),
      })
        .then((response) => {
          resolve(parseAutocompleteSuggestions(response.suggestions));
        })
        .catch(() => resolve([]));
      return;
    }

    const legacyService = places.AutocompleteService
      ? new places.AutocompleteService()
      : null;
    if (!legacyService) {
      resolve([]);
      return;
    }

    legacyService.getPlacePredictions(
      {
        input,
        types,
        ...(countryCode ? { componentRestrictions: { country: countryCode } } : {}),
        ...(language ? { language } : {}),
      },
      (predictions, status) => {
        if (status !== 'OK' || !predictions) {
          resolve([]);
          return;
        }

        const suggestions = predictions
          .map((item) => item.structured_formatting?.main_text ?? item.description ?? '')
          .map((item) => item.trim())
          .filter(Boolean);
        resolve(Array.from(new Set(suggestions)));
      },
    );
  });
}

function useAddressAutocomplete({
  apiKey,
  language,
  city,
  postcode,
  addressLine1,
  selectedCountryCode = '',
  selectedCountryName = '',
  cityDropdownOpen,
  maxEmptyStreetSuggestions = 5,
}: UseAddressAutocompleteParams): UseAddressAutocompleteResult {
  const [googleReady, setGoogleReady] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const [postcodeCitySuggestions, setPostcodeCitySuggestions] = useState<string[]>([]);
  const [streetSuggestions, setStreetSuggestions] = useState<string[]>([]);
  const cityTimer = useRef<number | null>(null);
  const streetTimer = useRef<number | null>(null);
  const postcodeTimer = useRef<number | null>(null);

  const cityDropdownOptions = useMemo(
    () => Array.from(new Set([...postcodeCitySuggestions, ...citySuggestions])),
    [citySuggestions, postcodeCitySuggestions],
  );

  useEffect(() => {
    if (!apiKey) {
      setGoogleReady(false);
      return;
    }

    let mounted = true;
    loadGoogleMapsPlacesScript(apiKey, language)
      .then(() => {
        if (mounted) {
          setGoogleReady(true);
        }
      })
      .catch(() => {
        if (mounted) {
          setGoogleReady(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [apiKey, language]);

  useEffect(() => () => {
    if (cityTimer.current) {
      window.clearTimeout(cityTimer.current);
    }
    if (streetTimer.current) {
      window.clearTimeout(streetTimer.current);
    }
    if (postcodeTimer.current) {
      window.clearTimeout(postcodeTimer.current);
    }
  }, []);

  useEffect(() => {
    if (!googleReady) {
      return;
    }

    const normalizedCity = city.trim();
    const normalizedPostcode = postcode.trim();
    const query = (
      normalizedCity.length >= 2
        ? normalizedCity
        : normalizedPostcode.length >= 2
          ? [normalizedPostcode, selectedCountryName].filter(Boolean).join(', ')
          : selectedCountryName
            ? `${selectedCountryName} city`
            : cityDropdownOpen
              ? 'a'
              : ''
    );

    if (!query) {
      if (!normalizedCity) {
        setCitySuggestions([]);
      }
      return;
    }

    if (cityTimer.current) {
      window.clearTimeout(cityTimer.current);
    }

    cityTimer.current = window.setTimeout(() => {
      void getPlacePredictions({
        input: query,
        types: ['(cities)'],
        countryCode: selectedCountryCode || undefined,
        language: language || undefined,
      }).then((suggestions) => {
        setCitySuggestions(Array.from(new Set(suggestions)));
      });
    }, 350);
  }, [city, cityDropdownOpen, googleReady, language, postcode, selectedCountryCode, selectedCountryName]);

  useEffect(() => {
    if (!googleReady || !city.trim()) {
      setStreetSuggestions([]);
      return;
    }

    if (streetTimer.current) {
      window.clearTimeout(streetTimer.current);
    }

    streetTimer.current = window.setTimeout(() => {
      const streetQuery = addressLine1.trim();
      const queryParts = streetQuery
        ? [streetQuery, city, selectedCountryName].filter(Boolean)
        : [city, selectedCountryName].filter(Boolean);

      void getPlacePredictions({
        input: queryParts.join(', '),
        types: ['address'],
        countryCode: selectedCountryCode || undefined,
        language: language || undefined,
      }).then((suggestions) => {
        const uniqueSuggestions = Array.from(new Set(suggestions));
        setStreetSuggestions(
          streetQuery ? uniqueSuggestions : uniqueSuggestions.slice(0, maxEmptyStreetSuggestions),
        );
      });
    }, 350);
  }, [addressLine1, city, googleReady, language, maxEmptyStreetSuggestions, selectedCountryCode, selectedCountryName]);

  useEffect(() => {
    if (!googleReady || postcode.trim().length < 3) {
      setPostcodeCitySuggestions([]);
      return;
    }

    if (postcodeTimer.current) {
      window.clearTimeout(postcodeTimer.current);
    }

    postcodeTimer.current = window.setTimeout(() => {
      const mapsWindow = window as GoogleMapsWindow;
      const geocoder = mapsWindow.google?.maps ? new mapsWindow.google.maps.Geocoder() : null;
      if (!geocoder) {
        return;
      }

      const query = [postcode, selectedCountryName].filter(Boolean).join(', ');
      geocoder.geocode({ address: query, ...(language ? { language } : {}) }, (results, status) => {
        if (status !== 'OK' || !results?.length) {
          setPostcodeCitySuggestions([]);
          return;
        }

        const normalizedPostcode = normalizePostcode(postcode);
        const rankedCandidates = results
          .map((result) => ({
            city: extractCityFromGeocodeResult(result).trim(),
            score: getGeocodeCityScore(result, {
              postcode: normalizedPostcode,
              countryCode: selectedCountryCode,
            }),
          }))
          .filter((item) => item.city)
          .sort((a, b) => b.score - a.score);

        const uniqueCities: string[] = [];
        const seen = new Set<string>();
        for (const item of rankedCandidates) {
          if (!seen.has(item.city)) {
            seen.add(item.city);
            uniqueCities.push(item.city);
          }
        }

        setPostcodeCitySuggestions(uniqueCities);
      });
    }, 450);
  }, [googleReady, language, postcode, selectedCountryCode, selectedCountryName]);

  return {
    cityDropdownOptions,
    streetSuggestions,
  };
}

export default useAddressAutocomplete;
